/**
 * 通用样式 - 工具类部分
 *
 * 间距、宽度、颜色、背景、边框、溢出处理、交互、图标、分隔线等工具类样式。
 * 由 common.ts 组合使用，不直接对外导出。
 */

import type { StyleDeps } from './style-types';

/**
 * 工具类样式定义
 */
export function utilityStyles({ token: t, css }: StyleDeps) {
  return {
    // ==================== 操作 ====================

    /** 操作按钮右对齐 */
    actionsRight: css`
      margin-left: auto;
    `,

    // ==================== 间距 (margin) ====================

    /** 左间距 - 小 */
    marginLeftSm: css`
      margin-left: ${t.marginXS}px;
    `,

    /** 上间距 - 小 */
    marginTopSm: css`
      margin-top: ${t.marginXS}px;
    `,

    /** 下间距 - 小 */
    marginBottomSm: css`
      margin-bottom: ${t.marginXS}px;
    `,

    /** 下间距 - 中 */
    marginBottomMd: css`
      margin-bottom: ${t.marginMD}px;
    `,

    /** 下间距 - 大 */
    marginBottomLg: css`
      margin-bottom: ${t.marginLG}px;
    `,

    /** 上间距 - 中 */
    marginTopMd: css`
      margin-top: ${t.marginMD}px;
    `,

    /** 上间距 - 大 */
    marginTopLg: css`
      margin-top: ${t.marginLG}px;
    `,

    /** 左间距 - 中 */
    marginLeftMd: css`
      margin-left: ${t.marginMD}px;
    `,

    /** 右间距 - 中 */
    marginRightMd: css`
      margin-right: ${t.marginMD}px;
    `,

    // ==================== 间距 (padding) ====================

    /** 内边距 - 小 */
    paddingSm: css`
      padding: ${t.paddingSM}px;
    `,

    /** 内边距 - 中 */
    paddingMd: css`
      padding: ${t.paddingMD}px;
    `,

    /** 内边距 - 大 */
    paddingLg: css`
      padding: ${t.paddingLG}px;
    `,

    /** 水平内边距 - 大 */
    paddingHorizontalLg: css`
      padding-left: ${t.paddingLG}px;
      padding-right: ${t.paddingLG}px;
    `,

    /** 垂直内边距 - 中 */
    paddingVerticalMd: css`
      padding-top: ${t.paddingMD}px;
      padding-bottom: ${t.paddingMD}px;
    `,

    // ==================== 宽度 ====================

    /** 全宽度 */
    fullWidth: css`
      width: 100%;
    `,

    /** 固定宽度 - 150px */
    width150: css`
      width: 150px;
    `,

    /** 固定宽度 - 200px */
    width200: css`
      width: 200px;
    `,

    /** 固定宽度 - 400px */
    width400: css`
      width: 400px;
    `,

    /** 最大宽度容器 - 适中 */
    maxWidthMd: css`
      max-width: 800px;
      width: 100%;
    `,

    /** 最大宽度容器 - 大 */
    maxWidthLg: css`
      max-width: 1000px;
      width: 100%;
    `,

    /** 自动换行 */
    wordBreak: css`
      word-break: break-all;
    `,

    // ==================== 颜色 ====================

    /** 主色 */
    colorPrimary: css`
      color: ${t.colorPrimary};
    `,

    /** 成功色 */
    colorSuccess: css`
      color: ${t.colorSuccess};
    `,

    /** 警告色 */
    colorWarning: css`
      color: ${t.colorWarning};
    `,

    /** 错误色 */
    colorError: css`
      color: ${t.colorError};
    `,

    /** 文本次要色 */
    colorTextSecondary: css`
      color: ${t.colorTextSecondary};
    `,

    /** 文本三级色 */
    colorTextTertiary: css`
      color: ${t.colorTextTertiary};
    `,

    // ==================== 边框 ====================

    /** 圆角 - 小 */
    borderRadiusSm: css`
      border-radius: ${t.borderRadiusSM}px;
    `,

    /** 圆角 - 基础 */
    borderRadiusBase: css`
      border-radius: ${t.borderRadius}px;
    `,

    /** 圆角 - 大 */
    borderRadiusLg: css`
      border-radius: ${t.borderRadiusLG}px;
    `,

    /** 底边框 */
    borderBottom: css`
      border-bottom: 1px solid ${t.colorBorderSecondary};
    `,

    /** 左边框 - 选中状态 */
    borderLeftActive: css`
      border-left: 3px solid ${t.colorPrimary};
    `,

    // ==================== 背景 ====================

    /** 背景 - 容器色 */
    bgContainer: css`
      background: ${t.colorBgContainer};
    `,

    /** 背景 - 布局色 */
    bgLayout: css`
      background: ${t.colorBgLayout};
    `,

    /** 背景 - 主色浅底 */
    bgPrimaryLight: css`
      background: ${t.colorPrimaryBg};
    `,

    // ==================== 溢出处理 ====================

    /** 单行省略 */
    ellipsis: css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `,

    /** 多行省略（2行） */
    ellipsis2: css`
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    `,

    /** 隐藏溢出 */
    overflowHidden: css`
      overflow: hidden;
    `,

    /** 自动滚动 */
    overflowAuto: css`
      overflow: auto;
    `,

    // ==================== 交互 ====================

    /** 鼠标指针 */
    cursorPointer: css`
      cursor: pointer;
    `,

    /** 默认鼠标 */
    cursorDefault: css`
      cursor: default;
    `,

    /** 禁用状态 */
    disabled: css`
      opacity: 0.5;
      pointer-events: none;
    `,

    // ==================== 分隔线 ====================

    /** 水平分隔线 */
    dividerHorizontal: css`
      margin: ${t.marginXS}px 0;
      border-top: 1px solid ${t.colorBorderSecondary};
    `,

    // ==================== 图标 ====================

    /** 图标大小 - 16px */
    iconSize16: css`
      font-size: 16px;
      width: 16px;
      height: 16px;
    `,

    /** 图标大小 - 20px */
    iconSize20: css`
      font-size: 20px;
      width: 20px;
      height: 20px;
    `,

    /** 图标大小 - 24px */
    iconSize24: css`
      font-size: 24px;
      width: 24px;
      height: 24px;
    `,
  };
}
